'use client';

// useContentInput hook - Form management for content input using React Hook Form + Zod

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  contentInputSchema,
  type ContentInputData,
  MAX_CONTENT_LENGTH,
} from './validation';

export interface UseContentInputReturn {
  /** Current content value */
  content: string;
  /** Character count of current content */
  characterCount: number;
  /** Maximum allowed characters */
  maxCharacters: number;
  /** Whether content exceeds max length */
  isOverLimit: boolean;
  /** Form error message key (for i18n) */
  errorKey: string | null;
  /** Whether form is valid */
  isValid: boolean;
  /** Register function for input field */
  register: ReturnType<typeof useForm<ContentInputData>>['register'];
  /** Handle content submission */
  handleSubmit: (
    onSubmit: (data: ContentInputData) => void | Promise<void>
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
  /** Reset form to initial state */
  reset: () => void;
  /** Set content value programmatically */
  setValue: (content: string) => void;
}

/**
 * Hook for managing content input form state
 *
 * @example
 * ```tsx
 * const { content, characterCount, register, handleSubmit } = useContentInput();
 *
 * return (
 *   <form onSubmit={handleSubmit(onSubmit)}>
 *     <textarea {...register('content')} />
 *     <span>{characterCount} / {maxCharacters}</span>
 *   </form>
 * );
 * ```
 */
export function useContentInput(): UseContentInputReturn {
  const form = useForm<ContentInputData>({
    resolver: zodResolver(contentInputSchema),
    defaultValues: {
      content: '',
    },
    mode: 'onChange',
  });

  const content = form.watch('content') || '';
  const characterCount = content.length;
  const isOverLimit = characterCount > MAX_CONTENT_LENGTH;
  const errorKey = form.formState.errors.content?.message || null;
  const isValid = form.formState.isValid && characterCount > 0;

  const setValue = (newContent: string) => {
    form.setValue('content', newContent, { shouldValidate: true });
  };

  return {
    content,
    characterCount,
    maxCharacters: MAX_CONTENT_LENGTH,
    isOverLimit,
    errorKey,
    isValid,
    register: form.register,
    handleSubmit: form.handleSubmit,
    reset: () => form.reset(),
    setValue,
  };
}
